// Meta Pixel, consent-gated. Pure browser helpers, safe to import from any
// client component; every function is a no-op on the server, before consent,
// or when the pixel failed to load.
//
// Dataset "munecas-dataset" (owner: Munecas Inc business portfolio
// 2066967397269310), created 2026-11-03. Events sent from this site:
// PageView, ViewContent, AddToCart, InitiateCheckout. Purchase is NOT sent
// here: checkout and the thank-you step run on Wix's hosted checkout, so the
// Purchase event has to come from Wix's own Meta integration (same dataset ID)
// or a server-side relay. Never both: they would double-count.
//
// content_ids use the Wix product ID with content_type "product_group", which
// matches g:item_group_id in /feeds/google.xml, the catalog feed Meta reads.

'use client';

export const META_PIXEL_ID = "1839607413898862";

const CONSENT_KEY = "mc:consent";
export const CONSENT_EVENT = "mc:consent-change";

export type Consent = "granted" | "denied";

type Fbq = ((...args: unknown[]) => void) & {
  callMethod?: (...args: unknown[]) => void;
  queue?: unknown[];
  loaded?: boolean;
  version?: string;
  push?: unknown;
};

declare global {
  interface Window {
    fbq?: Fbq;
    _fbq?: Fbq;
  }
}

export function getConsent(): Consent | null {
  if (typeof window === "undefined") return null;
  try {
    const v = window.localStorage.getItem(CONSENT_KEY);
    return v === "granted" || v === "denied" ? v : null;
  } catch {
    return null;
  }
}

/** Store the shopper's choice (null = ask again) and tell the banner + loader. */
export function setConsent(v: Consent | null): void {
  if (typeof window === "undefined") return;
  try {
    if (v) window.localStorage.setItem(CONSENT_KEY, v);
    else window.localStorage.removeItem(CONSENT_KEY);
  } catch {}
  window.dispatchEvent(new Event(CONSENT_EVENT));
}

let loaded = false;

/** Inject fbevents.js once and init the pixel. Only after consent. */
export function loadPixel(): void {
  if (typeof window === "undefined" || loaded || getConsent() !== "granted") return;
  loaded = true;
  // Meta's standard bootstrap, written out instead of pasted minified.
  if (!window.fbq) {
    const q: Fbq = function (...args: unknown[]) {
      if (q.callMethod) q.callMethod(...args);
      else q.queue!.push(args);
    } as Fbq;
    q.push = q;
    q.loaded = true;
    q.version = "2.0";
    q.queue = [];
    window.fbq = q;
    window._fbq = q;
    const s = document.createElement("script");
    s.async = true;
    s.src = "https://connect.facebook.net/en_US/fbevents.js";
    document.head.appendChild(s);
  }
  window.fbq("init", META_PIXEL_ID);
}

function fbq(...args: unknown[]): void {
  // Any event can arrive before MetaPixel's effect has run (child effects run
  // first); loadPixel() is idempotent and itself refuses without consent.
  loadPixel();
  if (!loaded || typeof window === "undefined" || !window.fbq) return;
  try {
    window.fbq(...args);
  } catch {}
}

export function trackPageView(): void {
  fbq("track", "PageView");
}

export function trackViewContent(p: { id: string; name: string; price: number | null }): void {
  fbq("track", "ViewContent", {
    content_ids: [p.id],
    content_type: "product_group",
    content_name: p.name,
    ...(p.price != null ? { value: p.price, currency: "USD" } : {}),
  });
}

export function trackAddToCart(p: { id: string; quantity: number }): void {
  fbq("track", "AddToCart", {
    content_ids: [p.id],
    content_type: "product_group",
    contents: [{ id: p.id, quantity: p.quantity }],
    currency: "USD",
  });
}

export function trackInitiateCheckout(c: { value: number | null; numItems: number }): void {
  fbq("track", "InitiateCheckout", {
    num_items: c.numItems,
    currency: "USD",
    ...(c.value != null ? { value: c.value } : {}),
  });
}
